import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  Chip,
  CircularProgress,
  TableFooter,
  TablePagination
} from '@material-ui/core';
import SearchBar from 'material-ui-search-bar';
import TablePaginationActions from './TablePaginationActions';

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: '#202340',
    color: theme.palette.common.white
  },
  body: {
    fontSize: 14
  }
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover
    }
  }
}))(TableRow);

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    width: '100%'
  },
  searchBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3)
  },
  tableActionItems: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'end'
  },
  table: {
    display: 'block',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    // minWidth: 1200,
    width: '100%'
  },
  body: {
    width: '100%'
  }
}));

function CustomDataTable({
  tableData,
  className,
  loading,
  tableConfig,
  filterField,
  tableActions
}) {
  const classes = useStyles();
  const [searched, setSearched] = React.useState('');
  const [filteredRows, setFilteredRows] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // const emptyRows =
  //   rowsPerPage - Math.min(rowsPerPage, tableData.length - page * rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const requestSearch = (searchedValue) => {
    if (searchedValue === '') {
      setFilteredRows(tableData);
    }

    const filterRows = tableData.filter((row) => {
      const result = row[filterField]
        ?.toLowerCase()
        ?.includes(searchedValue.toLowerCase());
      return result;
    });
    setFilteredRows(filterRows);
  };

  const cancelSearch = () => {
    setSearched('');
    requestSearch(searched);
    setFilteredRows(tableData);
  };

  useEffect(() => {
    setFilteredRows(tableData);
  }, [tableData]);

  return (
    <>
      <Box className={classes.tableActionItems}>
        {tableActions && <Box>{tableActions}</Box>}
        {filterField && (
          <Box>
            <SearchBar
              value={searched}
              onChange={(searchedVal) => requestSearch(searchedVal)}
              onCancelSearch={() => cancelSearch()}
              placeholder={`Search ${filterField ? 'by ' + filterField : ''}`}
            />
          </Box>
        )}
      </Box>
      <TableContainer className={classes.container} component={Paper}>
        <Table className={classes.table}>
          <TableHead style={{ width: '100%' }}>
            <TableRow style={{ width: '100%' }}>
              {tableConfig.map((item, index) => (
                <StyledTableCell
                  key={index}
                  align="center"
                  style={
                    index == tableConfig.length - 1
                      ? { width: '100%' }
                      : { width: '' }
                  }
                >
                  {item.title}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          {!loading ? (
            <TableBody className={classes.body}>
              {filteredRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, index) => {
                  return (
                    <StyledTableRow key={index}>
                      {tableConfig.map((head, i) => {
                        const { align, value, title, render } = head;
                        const res = item[value] ? item[value] : null;

                        const currentRowItemIndexAcrossPages =
                          page * rowsPerPage + index;
                        return (
                          <TableCell
                            key={i}
                            align={align ? align : ''}
                            // style={{ width: '100%' }}
                          >
                            {render
                              ? render(item, currentRowItemIndexAcrossPages)
                              : res}
                          </TableCell>
                        );
                      })}
                    </StyledTableRow>
                  );
                })}
            </TableBody>
          ) : (
            <TableBody>
              <TableCell>
                <CircularProgress size="1rem" />
              </TableCell>
            </TableBody>
          )}
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                colSpan={3}
                count={filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: { 'aria-label': 'rows per page' },
                  native: true
                }}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </>
  );
}

CustomDataTable.propTypes = {
  tableConfig: PropTypes.array.isRequired,
  tableData: PropTypes.array.isRequired,
  className: PropTypes.object,
  loading: PropTypes.bool,
  filterField: PropTypes.string,
  tableActions: PropTypes.func
};

export default CustomDataTable;
